import { FC, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button, ButtonProps } from 'antd';
import {
  DataObjectGroupWithDescriptionsDto,
  SampleDataObjectValidFormDataType,
  SampleValidFormDataType,
  useAdditionalFormContext,
} from '@atlas-frontend/sdk';
import { useSampleContext } from '../state/SampleContext';
import { SampleSubjectLockIcon } from './SampleSubjectLockIcon';
import { TooltipForSome } from './TooltipForSome';

export interface SampleSubjectLockButtonProps {
  formDataIndex: number;
  readOnly?: boolean;
}

export const SampleSubjectLockButton: FC<SampleSubjectLockButtonProps> = (
  props,
) => {
  const { formDataIndex, readOnly } = props;

  if (isNaN(formDataIndex)) {
    throw new Error('formDataIndex is not a number');
  }

  const id = `lock-sampleDataSubject-${formDataIndex}`;

  return readOnly ? (
    <SampleSubjectLockButtonReadOnly id={id} formDataIndex={formDataIndex} />
  ) : (
    <SampleSubjectLockButtonInteractive id={id} formDataIndex={formDataIndex} />
  );
};

interface SampleSubjectLockButtonReadOnlyProps {
  id: string;
  formDataIndex: number;
}

export const SampleSubjectLockButtonReadOnly: FC<
  SampleSubjectLockButtonReadOnlyProps
> = (props) => {
  const { id, formDataIndex } = props;
  const { getValues } = useFormContext<SampleValidFormDataType>();
  const [isWholeSampleLocked, sampleDataObjects] = getValues([
    `samples.${formDataIndex}.isLocked`,
    `samples.${formDataIndex}.sampleDataObjects`,
  ]);
  const { sampleType, selectedGroupMemberId } = useSampleContext();

  return sampleType === 'description' ? (
    <DescriptionLockButton
      id={id}
      isLocked={isWholeSampleLocked}
      hasActive={sampleDataObjects.some((o) => o.isLocked && o.hasActive)}
    />
  ) : selectedGroupMemberId ? (
    <GroupMemberLockButton
      id={id}
      selectedGroupMemberId={selectedGroupMemberId}
      isGroupLocked={isWholeSampleLocked}
      sampleDataObjects={sampleDataObjects}
    />
  ) : (
    <GroupWideLockButton
      id={id}
      isGroupLocked={isWholeSampleLocked}
      sampleDataObjects={sampleDataObjects}
    />
  );
};

interface SampleSubjectLockButtonInteractiveProps {
  id: string;
  formDataIndex: number;
}

export const SampleSubjectLockButtonInteractive: FC<
  SampleSubjectLockButtonInteractiveProps
> = (props) => {
  const { id, formDataIndex } = props;
  const { setValue } = useFormContext<SampleValidFormDataType>();
  const { onChange: formOnChange } = useAdditionalFormContext();

  const name = useMemo(
    () =>
      [
        `samples.${formDataIndex}.isLocked`,
        `samples.${formDataIndex}.sampleDataObjects`,
      ] as const,
    [formDataIndex],
  );

  const [isWholeSampleLocked, sampleDataObjects] = useWatch<
    SampleValidFormDataType,
    [`samples.${number}.isLocked`, `samples.${number}.sampleDataObjects`]
  >({ name });

  const setWholeSampleLock = useCallback(
    (isGroupLocked: boolean) => {
      setValue(name[0], isGroupLocked, { shouldDirty: true });
      formOnChange?.();
    },
    [formOnChange, name, setValue],
  );

  const setGroupMemberLocks = useCallback(
    (locks: SampleDataObjectValidFormDataType[]) => {
      setValue(name[1], locks, { shouldDirty: true });
      formOnChange?.();
    },
    [formOnChange, name, setValue],
  );

  const { sampleType, selectedGroupMemberId } = useSampleContext();

  return sampleType === 'description' ? (
    <DescriptionLockButton
      id={id}
      isLocked={isWholeSampleLocked}
      hasActive={sampleDataObjects.some((o) => o.isLocked && o.hasActive)}
      onChange={setWholeSampleLock}
    />
  ) : selectedGroupMemberId ? (
    <GroupMemberLockButton
      id={id}
      selectedGroupMemberId={selectedGroupMemberId}
      isGroupLocked={isWholeSampleLocked}
      sampleDataObjects={sampleDataObjects}
      onChange={setGroupMemberLocks}
    />
  ) : (
    <GroupWideLockButton
      id={id}
      isGroupLocked={isWholeSampleLocked}
      sampleDataObjects={sampleDataObjects}
      onChange={setWholeSampleLock}
    />
  );
};

interface DescriptionLockButtonProps {
  id: string;
  isLocked: boolean;
  hasActive: boolean;
  onChange?: (isLocked: boolean) => void;
}

// Sample is based on specific data object description
const DescriptionLockButton: FC<DescriptionLockButtonProps> = (props) => {
  const { id, isLocked, hasActive, onChange } = props;

  const handleClick = useMemo(
    () =>
      onChange &&
      (() => {
        onChange(!isLocked);
      }),
    [isLocked, onChange],
  );

  return (
    <LockButton
      id={id}
      isLocked={isLocked || false}
      hasActive={hasActive}
      onClick={handleClick}
    />
  );
};

interface GroupWideLockButtonProps {
  id: string;
  isGroupLocked: boolean;
  sampleDataObjects: SampleDataObjectValidFormDataType[];
  onChange?: (isGroupLocked: boolean) => void;
}

// Sample is based on data object group and filter is also set to "whole group"
const GroupWideLockButton: FC<GroupWideLockButtonProps> = (props) => {
  const { id, isGroupLocked, sampleDataObjects, onChange } = props;
  const { groupOrDescription } = useSampleContext();

  const descriptions = (
    groupOrDescription as DataObjectGroupWithDescriptionsDto
  )?.descriptions;

  const isLocked: boolean | 'some' = useMemo(() => {
    const groupMembers = descriptions || [];
    return (
      isGroupLocked ||
      (groupMembers.some(
        ({ id }) =>
          sampleDataObjects?.find((o) => o.dataObjectDescriptionId === id)
            ?.isLocked,
      )
        ? 'some'
        : false)
    );
  }, [isGroupLocked, sampleDataObjects, descriptions]);

  const hasActive = useMemo(
    () => isLocked && sampleDataObjects.some((o) => o.isLocked && o.hasActive),
    [isLocked, sampleDataObjects],
  );

  const handleClick = useMemo(
    () =>
      onChange &&
      (() => {
        onChange(isLocked === 'some' ? true : !isLocked);
      }),
    [isLocked, onChange],
  );

  if (isLocked === 'some') {
    return (
      <TooltipForSome sampleDataObjects={sampleDataObjects}>
        <LockButton
          id={id}
          isLocked={isLocked}
          hasActive={hasActive}
          onClick={handleClick}
        />
      </TooltipForSome>
    );
  }

  return <LockButton id={id} isLocked={isLocked} onClick={handleClick} />;
};

interface GroupMemberLockButtonProps {
  id: string;
  isGroupLocked: boolean;
  sampleDataObjects: SampleDataObjectValidFormDataType[];
  selectedGroupMemberId: number;
  onChange?: (locks: SampleDataObjectValidFormDataType[]) => void;
}

// Sample is based on data object group but filter is set to a specific data object
const GroupMemberLockButton: FC<GroupMemberLockButtonProps> = (props) => {
  const {
    id,
    isGroupLocked,
    sampleDataObjects,
    selectedGroupMemberId,
    onChange,
  } = props;

  const isLocked = useMemo(
    () =>
      isGroupLocked ||
      sampleDataObjects.find(
        (o) => o.dataObjectDescriptionId === selectedGroupMemberId,
      )?.isLocked ||
      false,
    [isGroupLocked, sampleDataObjects, selectedGroupMemberId],
  );

  const hasActive = useMemo(
    () =>
      sampleDataObjects.some(
        (o) =>
          o.dataObjectDescriptionId === selectedGroupMemberId &&
          o.isLocked &&
          o.hasActive,
      ),
    [sampleDataObjects, selectedGroupMemberId],
  );

  const handleClick = useMemo(
    () =>
      onChange &&
      (() => {
        let isExisting = false;

        const newSampleDataObjects = sampleDataObjects.reduce(
          (acc, dataObject) => {
            const isSelected =
              dataObject.dataObjectDescriptionId === selectedGroupMemberId;

            const objects = acc.concat({
              ...dataObject,
              isLocked: isSelected ? !dataObject.isLocked : dataObject.isLocked,
            });

            if (isSelected) {
              isExisting = true;
            }

            return objects;
          },
          [] as SampleDataObjectValidFormDataType[],
        );

        if (!isExisting) {
          newSampleDataObjects.push({
            dataObjectDescriptionId: selectedGroupMemberId,
            isLocked: true,
            comments: '',
            hasActive: false,
          });
        }

        onChange(newSampleDataObjects);
      }),
    [onChange, sampleDataObjects, selectedGroupMemberId],
  );

  return (
    <LockButton
      id={id}
      isLocked={isLocked}
      hasActive={hasActive}
      disabled={isGroupLocked}
      onClick={handleClick}
    />
  );
};

interface LockButtonProps extends ButtonProps {
  isLocked: boolean | 'some';
  hasActive?: boolean;
}

const LockButton: FC<LockButtonProps> = ({
  isLocked,
  hasActive,
  onClick,
  ...rest
}) =>
  onClick ? (
    <Button type="text" shape="circle" size="small" onClick={onClick} {...rest}>
      <SampleSubjectLockIcon isLocked={isLocked} showBullet={hasActive} />
    </Button>
  ) : (
    <SampleSubjectLockIcon isLocked={isLocked} showBullet={hasActive} />
  );
